import { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  useSiteBlueprints,
  useCreateSiteBlueprint,
  useUpdateSiteBlueprint,
  useReplaceSiteBlueprintFile,
  useDeleteSiteBlueprint,
} from '../../../hooks/queries/useSites';
import type { SiteBlueprint } from '../../../services';
import { toaster } from '../../ui/toaster';
import {
  BlueprintsTableView,
  type BlueprintRowVM,
} from './BlueprintsTableView';
import { ACCEPTED_EXTS, validate } from './blueprintValidation';

interface BlueprintsSectionProps {
  siteId: number;
}

export function BlueprintsSection({ siteId }: BlueprintsSectionProps) {
  const { t } = useTranslation();
  const addInputRef = useRef<HTMLInputElement | null>(null);
  const replaceInputRef = useRef<HTMLInputElement | null>(null);
  const replaceTargetRef = useRef<number | null>(null);

  const notifyError = (title: string) => (err: unknown) => {
    toaster.create({
      title,
      description: err instanceof Error ? err.message : t('common.somethingWentWrong', 'Something went wrong'),
      type: 'error',
      duration: 4000,
    });
  };

  const { data: blueprints = [], isLoading, isError } = useSiteBlueprints(
    siteId,
  );
  const createMutation = useCreateSiteBlueprint();
  const updateMutation = useUpdateSiteBlueprint();
  const replaceMutation = useReplaceSiteBlueprintFile();
  const deleteMutation = useDeleteSiteBlueprint();

  const handleAdd = (file: File) => {
    createMutation.mutate(
      { siteId, file, label: file.name },
      {
        onSuccess: () => {
          toaster.create({
            title: t('sites.blueprintUploaded', 'Blueprint uploaded'),
            type: 'success',
            duration: 3000,
          });
        },
        onError: notifyError(t('sites.uploadFailed', 'Upload failed')),
      },
    );
  };

  const handleReplace = (blueprintId: number, file: File) => {
    replaceMutation.mutate(
      { siteId, blueprintId, file },
      {
        onSuccess: () => {
          toaster.create({
            title: t('sites.blueprintReplaced', 'Blueprint replaced'),
            description: t('sites.statusResetToPending', 'Status reset to Pending.'),
            type: 'success',
            duration: 3000,
          });
        },
        onError: notifyError(t('sites.replaceFailed', 'Replace failed')),
      },
    );
  };

  const handleDelete = (id: string) => {
    deleteMutation.mutate(
      { siteId, blueprintId: Number(id) },
      {
        onSuccess: () => {
          toaster.create({
            title: t('sites.blueprintRemoved', 'Blueprint removed'),
            type: 'success',
            duration: 3000,
          });
        },
        onError: notifyError(t('sites.deleteFailed', 'Delete failed')),
      },
    );
  };

  const handleEditLabel = (id: string, label: string) => {
    updateMutation.mutate(
      { siteId, blueprintId: Number(id), patch: { label } },
      { onError: notifyError(t('sites.renameFailed', 'Rename failed')) },
    );
  };

  const rows: BlueprintRowVM[] = blueprints.map((bp: SiteBlueprint) => ({
    id: String(bp.id),
    label: bp.label,
    fileName: bp.file_name,
    status: bp.status,
    errorMessage: bp.error_message,
  }));

  return (
    <>
      <input
        ref={addInputRef}
        type="file"
        accept={ACCEPTED_EXTS}
        hidden
        aria-label={t('sites.uploadNewBlueprintAria', 'Upload new blueprint')}
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (validate(file)) handleAdd(file);
          e.target.value = '';
        }}
      />
      <input
        ref={replaceInputRef}
        type="file"
        accept={ACCEPTED_EXTS}
        hidden
        aria-label={t('sites.replaceBlueprintFileAria', 'Replace blueprint file')}
        onChange={(e) => {
          const file = e.target.files?.[0];
          const target = replaceTargetRef.current;
          replaceTargetRef.current = null;
          if (target != null && validate(file)) handleReplace(target, file);
          e.target.value = '';
        }}
      />
      <BlueprintsTableView
        rows={rows}
        isLoading={isLoading}
        isError={isError}
        addLabel={createMutation.isPending ? t('sites.uploading', 'Uploading...') : t('sites.addBlueprint', 'Add Blueprint')}
        addDisabled={createMutation.isPending}
        onAddFileClick={() => addInputRef.current?.click()}
        onEditLabel={handleEditLabel}
        onReplaceClick={(id) => {
          replaceTargetRef.current = Number(id);
          replaceInputRef.current?.click();
        }}
        onDelete={handleDelete}
        editingDisabled={updateMutation.isPending}
        replaceDisabled={replaceMutation.isPending}
        deleteDisabled={deleteMutation.isPending}
        helperText={t('sites.blueprintsHelperText', 'Reference artifacts attached to this site. Add as many as you like — a single PDF, multiple images, whatever describes the space.')}
      />
    </>
  );
}
